import { StationeryPaperSize } from '@apps/card/routes/CardCustomizer/steps/CardDesign/config';
import { useTranslation } from '@shared/core';
import type { Product } from './product.types';
import { useMemo } from 'react';

const A2_ENVELOPE_SKU = 'WJYOEA2W';
const A7_ENVELOPE_SKU = 'WJYOEA7W';

export const useProductTypes = (): Product[] => {
  const { tq } = useTranslation('stationery');

  return useMemo(
    () => [
      {
        type: 'stationeryCard',
        mappingFunction: 'taylormap',
        supportedQuantities: new Array(40).fill(null).map((_, index) => (index + 1) * 10),
        cardTypeSKU: {
          [StationeryPaperSize.A2]: {
            envelopeSKU: A2_ENVELOPE_SKU,
            cardType: 'FlatNoteCard',
          },
          [StationeryPaperSize.A7]: {
            envelopeSKU: A7_ENVELOPE_SKU,
            cardType: 'FlatNoteCard',
          },
        },
        translations: {
          displayName: tq`Classic Paper`,
          formatName: tq`Paper`,
          galleryPriceStringName: tq`Paper`,
        },
      },
      {
        type: 'digitalCard',
        defaultQuantity: 250,
        mappingFunction: 'joydigitalmap',
        supportedQuantities: [50, 100, 250, 500, 1000],
        translations: {
          displayName: tq`Premium Digital`,
          formatName: tq`Premium Digital Cards`,
          galleryPriceStringName: tq`Digital`,
        },
      },
      {
        type: 'napkin',
        defaultQuantity: 100,
        mappingFunction: 'taylormap',
        supportedQuantities: [50, 100, 150, 200, 250, 300, 350, 400, 450, 500],
        cardTypeSKU: {
          [StationeryPaperSize.napkin475]: {
            cardType: 'FlatNoteCard',
          },
          [StationeryPaperSize.napkin650]: {
            cardType: 'FlatNoteCard',
          },
        },
        translations: {
          displayName: tq`Napkins`,
          formatName: tq`Napkin`,
          galleryPriceStringName: tq`Napkins`,
        },
      },
      {
        type: 'sign',
        defaultQuantity: 100,
        mappingFunction: 'taylormap',
        supportedQuantities: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 20, 50],
        cardTypeSKU: {
          [StationeryPaperSize.foam18X24]: {
            cardType: 'FlatNoteCard',
          },
          [StationeryPaperSize.foam24X36]: {
            cardType: 'FlatNoteCard',
          },
        },
        translations: {
          displayName: tq`Signs`,
          formatName: tq`Foam Board Signs`,
          galleryPriceStringName: tq`Signs`,
        },
      },
      {
        type: 'digital',
        supportedQuantities: [1],
        mappingFunction: 'freedigitalmap',
        translations: {
          displayName: tq`Basic Digital`,
          formatName: tq`Basic Digital`,
          // Matches the premium-digital label so digital-only tiles read "Digital Free"
          // (with a colored "Free"), consistent with paper+digital tiles.
          galleryPriceStringName: tq`Digital`,
        },
      },
      {
        type: 'sampleKit',
        supportedQuantities: [1],
        mappingFunction: 'taylormap',
        translations: {
          displayName: tq`Sample Kit`,
          formatName: tq`Sample Kit`,
          galleryPriceStringName: tq`Sample Kit`,
        },
      },
    ],
    [tq],
  );
};
